import type {
  FoodOutDetailsListModel,
  KitchenAutoAssignmentOptionsModel,
  KitchenAutoAssignmentResultModel,
  KitchenStoveModel,
  KitchenScheduleSettingsModel,
} from '@/model/kitchen';
import { changeKitchenStatus, fetchConfiguredStoves } from '@/service/kitchen';
import { distributeStove } from '@/service/order';
import { canCookKitchenTasksTogether } from '@/service/kitchenTaskBatch';

import { isKitchenTaskScheduled } from '@/service/kitchenSchedule';

const noEnabledChefMessage = '当前暂无接单中的厨师，菜品将继续等待分配。';

const isUnfinishedTask = (task: FoodOutDetailsListModel): boolean =>
  ![8, 9, 999].includes(task.status) && task.kitchenStatus !== 10;

// 分配策略：同菜品三分钟内优先同灶台，否则未完成任务最少者优先。
// 服务员暂停和厨师暂停的任务都计入负载，不设队列数量上限。
export const selectAutoAssignmentStove = (
  stoves: readonly KitchenStoveModel[],
  tasks: readonly FoodOutDetailsListModel[],
  pendingTask: FoodOutDetailsListModel | undefined,
  scheduleSettings: KitchenScheduleSettingsModel,
): KitchenStoveModel | undefined => {
  const counts = new Map<number, number>();
  const matchingStoves = new Set<number>();
  for (const task of tasks) {
    if (!task.stoveId || !isUnfinishedTask(task) || isKitchenTaskScheduled(task, scheduleSettings))
      continue;
    const stoveId = Number(task.stoveId);
    counts.set(stoveId, (counts.get(stoveId) || 0) + 1);
    if (
      pendingTask &&
      [0, 5].includes(task.kitchenStatus) &&
      task.operateStatus !== 1 &&
      canCookKitchenTasksTogether(pendingTask, task)
    )
      matchingStoves.add(stoveId);
  }
  return stoves
    .filter((stove) => stove.stoveStatus === 0)
    .sort((a, b) => {
      const sameDishPriority =
        Number(matchingStoves.has(b.stoveId)) - Number(matchingStoves.has(a.stoveId));
      const loadDifference = (counts.get(a.stoveId) || 0) - (counts.get(b.stoveId) || 0);
      return sameDishPriority || loadDifference || a.stoveId - b.stoveId;
    })[0];
};

// 页面通过 shouldContinue 控制主屏资格和请求有效期，模块不依赖 Vue 状态。
// readQueue 必须读取本次 kitchenId 对应餐厅、厨房的最新未完成队列。
export const assignKitchenTasksAutomatically = async (
  options: KitchenAutoAssignmentOptionsModel,
): Promise<KitchenAutoAssignmentResultModel> => {
  const { kitchenId, tasks, stoves, readQueue, shouldContinue, verifyMaster, scheduleSettings } =
    options;
  const result: KitchenAutoAssignmentResultModel = { assigned: false, message: '' };
  if (!shouldContinue()) return result;
  if (!stoves.some((stove) => Number(stove.kitchenId) === kitchenId && stove.stoveStatus === 0)) {
    result.message = noEnabledChefMessage;
    return result;
  }

  for (const task of tasks.filter(
    (item) =>
      !item.stoveId && isUnfinishedTask(item) && !isKitchenTaskScheduled(item, scheduleSettings),
  )) {
    if (!shouldContinue()) break;
    // 每条任务分配前重读，避免给已下班厨师或已被其他屏分配的任务重复分配。
    const [stoveResponse, currentQueue] = await Promise.all([
      fetchConfiguredStoves(kitchenId),
      readQueue(),
    ]);
    if (!shouldContinue()) break;
    if (stoveResponse.code !== 200)
      throw new Error(stoveResponse.msg || '暂时无法确认厨师是否接单，请刷新重试');
    const currentTask = currentQueue.find(
      (item) =>
        item.orderOutId === task.orderOutId &&
        !item.stoveId &&
        isUnfinishedTask(item) &&
        !isKitchenTaskScheduled(item, scheduleSettings),
    );
    if (!currentTask) continue;

    const target = selectAutoAssignmentStove(
      (stoveResponse.result?.list || []).filter((stove) => Number(stove.kitchenId) === kitchenId),
      currentQueue,
      currentTask,
      scheduleSettings,
    );
    if (!target) {
      result.message = noEnabledChefMessage;
      break;
    }
    try {
      if (!(await verifyMaster()) || !shouldContinue()) break;
      const response = await distributeStove({
        orderOutId: task.orderOutId,
        stoveId: target.stoveId,
      });
      if (response.code !== 200) throw new Error(response.msg || '分配失败');
      result.assigned = true;
      // 分配接口可能改变制作状态，必须明确恢复为未开始；实际开工由厨师触发。
      const pendingResponse = await changeKitchenStatus({
        orderOutId: task.orderOutId,
        kitchenStatus: 0,
      });
      if (pendingResponse.code !== 200)
        throw new Error(pendingResponse.msg || '菜品已分配，暂未确认制作进度，请刷新查看');
    } catch (error) {
      console.error(`自动分配菜品 ${task.orderOutId} 失败`, error);
      result.message = '部分菜品未能完成分配，请刷新查看负责厨师和制作进度。';
    }
  }
  return result;
};
